import api from '@/plugins/api';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported as isMessagingSupported, onMessage } from 'firebase/messaging';
import { firebaseConfig, firebaseVapidKey, isFirebaseConfigValid } from '@/firebaseConfig';

class FCMService {
  constructor() {
    this.fcmToken = null;
    this.oldToken = null; // Ancien token pour détecter les changements
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this._firebaseApp = null;
    this._messaging = null;
    this._tokenRefreshHandler = null;
  }

  /**
   * Initialiser le service FCM
   */
  async initialize() {
    if (!this.isSupported) {
      throw new Error('FCM non supporté par ce navigateur');
    }

    try {
      // Enregistrer le service worker
      this.registration = await navigator.serviceWorker.register('/sw-fcm.js');
      console.log('Service Worker FCM enregistré:', this.registration);

      // Demander la permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Permission de notification refusée');
      }

      // Initialiser Firebase
      await this.initializeFirebase();

      // Obtenir le token FCM réel
      await this.getFCMToken();

      // Démarrer la vérification périodique du token
      this.startTokenCheckInterval();

      return true;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation FCM:', error);
      throw error;
    }
  }

  /**
   * Démarrer la vérification périodique du token
   * Les tokens FCM sont généralement stables et ne changent pas fréquemment.
   * On vérifie toutes les heures + lors d'événements spécifiques (visibilitychange, focus)
   */
  startTokenCheckInterval() {
    // Vérifier le token toutes les heures (les tokens sont stables, pas besoin de vérifier trop souvent)
    if (this._tokenCheckInterval) {
      clearInterval(this._tokenCheckInterval);
    }

    this._tokenCheckInterval = setInterval(async () => {
      try {
        // Vérifier si l'utilisateur est connecté
        const user = localStorage.getItem('user');
        if (!user) {
          return; // Pas besoin de vérifier si l'utilisateur n'est pas connecté
        }

        // Récupérer le token actuel (getFCMToken() détecte automatiquement les changements et les met à jour)
        await this.getFCMToken();
      } catch (error) {
        console.debug('Erreur lors de la vérification périodique du token:', error);
      }
    }, 60 * 60 * 1000); // 1 heure (les tokens sont stables, pas besoin de vérifier toutes les 5 minutes)

    // Vérifier aussi lors d'événements spécifiques où le token pourrait changer
    this.setupEventListeners();
  }

  /**
   * Configurer les écouteurs d'événements pour détecter les changements de token
   * lors d'événements spécifiques (retour de veille, changement de focus, etc.)
   */
  setupEventListeners() {
    // Vérifier le token quand l'utilisateur revient sur la page (retour de veille)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const user = localStorage.getItem('user');
        if (user) {
          try {
            await this.getFCMToken(); // Vérifie automatiquement les changements
          } catch (error) {
            console.debug('Erreur lors de la vérification du token après retour de veille:', error);
          }
        }
      }
    };

    // Vérifier le token quand la fenêtre reprend le focus
    const handleFocus = async () => {
      const user = localStorage.getItem('user');
      if (user) {
        try {
          await this.getFCMToken(); // Vérifie automatiquement les changements
        } catch (error) {
          console.debug('Erreur lors de la vérification du token après focus:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Stocker les handlers pour pouvoir les retirer plus tard si nécessaire
    this._visibilityHandler = handleVisibilityChange;
    this._focusHandler = handleFocus;
  }

  /**
   * Arrêter la vérification périodique du token
   */
  stopTokenCheckInterval() {
    if (this._tokenCheckInterval) {
      clearInterval(this._tokenCheckInterval);
      this._tokenCheckInterval = null;
    }

    // Retirer les écouteurs d'événements
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    if (this._focusHandler) {
      window.removeEventListener('focus', this._focusHandler);
      this._focusHandler = null;
    }
  }

  async initializeFirebase() {
    if (!isFirebaseConfigValid()) {
      throw new Error('Configuration Firebase invalide (vérifiez les variables VUE_APP_FIREBASE_*)');
    }

    const supported = await isMessagingSupported().catch(() => false);
    if (!supported) {
      throw new Error('Firebase Messaging non supporté dans ce navigateur');
    }

    if (!this._firebaseApp) {
      this._firebaseApp = initializeApp(firebaseConfig);
    }
    if (!this._messaging) {
      this._messaging = getMessaging(this._firebaseApp);
      // Écouter les changements de token
      this.setupTokenRefreshListener();
      // Écouter les messages quand l'app est au premier plan
      this.setupMessageListener();
    }
  }

  /**
   * Configurer l'écouteur pour détecter les changements de token
   * 
   * IMPORTANT: Firebase Web SDK n'a pas d'événement onTokenRefresh() natif
   * comme sur mobile. On doit donc détecter les changements manuellement.
   * 
   * Méthodes de détection :
   * 1. Comparaison à chaque appel de getToken() - Firebase retourne toujours le token actuel
   * 2. Vérification périodique (toutes les heures)
   * 3. Vérification lors d'événements (visibilitychange, focus)
   * 4. Le Service Worker peut aussi notifier si le token change (via message)
   */
  setupTokenRefreshListener() {
    if (!this._messaging) return;

    // Écouter les messages du Service Worker qui pourrait notifier un changement de token
    if (this.registration && this.registration.active) {
      this.registration.active.postMessage({
        type: 'CHECK_TOKEN_REFRESH'
      });
    }

    // Note: La détection principale se fait dans getFCMToken() qui compare
    // le token retourné par Firebase avec celui stocké localement
  }

  /**
   * Configurer l'écouteur pour les messages FCM quand l'app est au premier plan
   * Quand l'application est active, Firebase n'affiche pas automatiquement les notifications.
   * On doit donc les afficher manuellement.
   */
  setupMessageListener() {
    if (!this._messaging) return;

    // Écouter les messages FCM quand l'app est au premier plan
    onMessage(this._messaging, (payload) => {
      console.log('Message FCM reçu (app au premier plan):', payload);
      console.log('Structure du payload:', {
        hasNotification: !!payload.notification,
        hasData: !!payload.data,
        hasMessage: !!payload.message,
        keys: Object.keys(payload)
      });

      // FCM v1 API envoie les données dans payload.message.notification et payload.message.data
      // Format attendu : { message: { notification: { title, body }, data: { ... } } }
      const message = payload.message || payload;
      const notificationData = message.notification || payload.notification || {};
      const data = message.data || payload.data || {};

      // Extraire le titre et le corps (priorité : notification > data > défaut)
      let title = notificationData.title || data.title || payload.title || 'Notification';
      let body = notificationData.body || data.body || payload.body || 'Nouvelle notification';
      const icon = notificationData.icon || data.icon || '/favicon.ico';

      // Si c'est une notification d'erreur, améliorer l'affichage
      if (data.type === 'error' && data.source === 'logs_monitor') {
        // Construire un body complet avec les informations disponibles
        let errorBody = '';
        
        // Si error_count est disponible, l'ajouter au début
        if (data.error_count) {
          const count = parseInt(data.error_count) || 0;
          if (count > 0) {
            errorBody = `${count} erreur(s) détectée(s)\n`;
          }
        }
        
        // Utiliser le body de base (sans les retours à la ligne multiples)
        const baseBody = body.trim().replace(/\r\n+/g, ' ').replace(/\n+/g, ' ').replace(/\r+/g, ' ').trim();
        errorBody += baseBody;
        
        // Extraire la liste des DB depuis error_db_list ou error_details
        let dbList = '';
        if (data.error_db_list && data.error_db_list.trim() !== '') {
          dbList = data.error_db_list.trim();
          console.log('[FCM] error_db_list disponible:', dbList);
        } else if (data.error_details && data.error_details.trim() !== '') {
          // Si error_db_list est vide, extraire les noms de DB depuis error_details
          // Format: "- Site / Site\r\n" ou "- DB1\r\n- DB2\r\n"
          const details = data.error_details.trim();
          console.log('[FCM] error_db_list vide, extraction depuis error_details:', details);
          
          // Extraire les noms de DB depuis error_details
          // Format: "- Site / Site\r\n" ou "- [DB Name]\r\n" ou "- DB1\r\n- DB2\r\n"
          const lines = details.split(/\r?\n/).filter(line => line.trim().length > 0);
          const dbNames = lines
            .map(line => {
              // Enlever le préfixe "- " ou "-" et les crochets éventuels
              let dbName = line.trim().replace(/^-\s*/, '').replace(/^\[/, '').replace(/\]$/, '').trim();
              return dbName;
            })
            .filter(db => db.length > 0);
          
          if (dbNames.length > 0) {
            dbList = dbNames.join(', ');
            console.log('[FCM] DB extraites depuis error_details:', dbList);
          }
        }
        
        // Si on a une liste de DB, l'ajouter au body
        if (dbList && dbList.trim() !== '') {
          // Vérifier si le body contient déjà la liste des DB
          const bodyNormalized = baseBody.replace(/\s+/g, ' ').toLowerCase();
          const dbListNormalized = dbList.replace(/\s+/g, ' ').toLowerCase();
          
          // Vérification rapide : si la liste complète normalisée est dans le body
          let bodyContainsDbList = bodyNormalized.includes(dbListNormalized);
          console.log('[FCM] Vérification rapide - bodyContainsDbList:', bodyContainsDbList);
          
          // Si la vérification rapide échoue, vérifier chaque DB individuellement
          if (!bodyContainsDbList) {
            const dbNames = dbList.split(',').map(db => db.trim()).filter(db => db.length > 0);
            console.log('[FCM] Vérification individuelle - dbNames:', dbNames);
            for (const dbName of dbNames) {
              if (bodyNormalized.includes(dbName.toLowerCase())) {
                bodyContainsDbList = true;
                console.log('[FCM] DB trouvée dans le body:', dbName);
                break;
              }
            }
          }
          
          if (!bodyContainsDbList) {
            console.log('[FCM] Ajout de la liste des DB au body');
            // Le body ne contient pas la liste, on l'ajoute
            // Remplacer le point final par deux-points si nécessaire
            if (errorBody.endsWith('.')) {
              errorBody = errorBody.slice(0, -1) + ':';
            } else if (!errorBody.endsWith(':')) {
              errorBody += ':';
            }
            // Formater la liste des DB (s'assurer qu'elle est séparée par des virgules)
            const dbListFormatted = dbList.replace(/\r\n/g, ', ').replace(/\n/g, ', ').replace(/\r/g, ', ')
                                          .replace(/,\s*,/g, ',') // Supprimer les virgules doubles
                                          .replace(/\s*,\s*/g, ', '); // Normaliser les espaces autour des virgules
            errorBody += '\n' + dbListFormatted;
            console.log('[FCM] Body après ajout de la liste:', errorBody);
          } else {
            console.log('[FCM] La liste des DB est déjà dans le body, pas d\'ajout nécessaire');
          }
        } else {
          console.log('[FCM] Aucune liste de DB disponible (error_db_list et error_details vides)');
        }
        
        body = errorBody;
      }

      console.log('Données extraites:', { title, body, icon, data });

      // Afficher la notification manuellement
      if ('Notification' in window && Notification.permission === 'granted') {
        const notificationOptions = {
          body: body,
          icon: icon,
          badge: '/badge.png',
          tag: data.tag || title,
          data: data,
          requireInteraction: false,
          silent: false
        };

        // Afficher la notification
        const notification = new Notification(title, notificationOptions);

        // Gérer le clic sur la notification
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          notification.close();

          // Déclencher un événement personnalisé pour que l'app puisse réagir
          window.dispatchEvent(new CustomEvent('fcmNotificationClick', {
            detail: { data, payload }
          }));

          // Optionnel : rediriger vers une page spécifique
          if (data.url) {
            window.location.href = data.url;
          }
        };

        // Fermer automatiquement après 5 secondes
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      // Déclencher un événement personnalisé pour que l'app puisse réagir
      window.dispatchEvent(new CustomEvent('fcmMessage', {
        detail: { payload, notification: { title, body, data } }
      }));
    });
  }

  /**
   * Demander la permission de notification
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Ce navigateur ne supporte pas les notifications');
    }

    let permission = Notification.permission;
    
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    return permission;
  }

  /**
   * Obtenir le token FCM
   * 
   * COMMENT L'APPAREIL DÉTECTE QUE LE TOKEN A CHANGÉ :
   * 
   * Firebase Web SDK ne fournit pas d'événement onTokenRefresh() comme sur mobile.
   * La détection se fait donc par COMPARAISON :
   * 
   * 1. On stocke le token actuel (dans this.fcmToken et localStorage)
   * 2. À chaque appel de getToken(), Firebase retourne TOUJOURS le token actuel
   * 3. On compare le token retourné avec celui stocké
   * 4. Si différent → le token a changé → on met à jour automatiquement
   * 
   * Cette méthode est fiable car :
   * - getToken() retourne toujours le token le plus récent
   * - Si Firebase a généré un nouveau token, getToken() le retournera
   * - La comparaison détecte immédiatement le changement
   */
  async getFCMToken() {
    try {
      if (!this._messaging) {
        await this.initializeFirebase();
      }

      // Firebase getToken() retourne TOUJOURS le token actuel (le plus récent)
      // Si le token a changé côté Firebase, cette fonction retournera le nouveau token
      const token = await getToken(this._messaging, {
        vapidKey: firebaseVapidKey,
        serviceWorkerRegistration: this.registration,
      });

      if (!token) {
        throw new Error('Impossible d\'obtenir un token FCM');
      }

      // DÉTECTION DU CHANGEMENT : Comparer le token retourné avec celui stocké
      const oldToken = this.fcmToken || localStorage.getItem('fcm_token');
      
      if (oldToken && oldToken !== token) {
        // Le token a changé ! Firebase a généré un nouveau token
        console.log('🔄 Token FCM détecté comme changé (ancien vs nouveau)');
        console.log('   Ancien:', oldToken.substring(0, 30) + '...');
        console.log('   Nouveau:', token.substring(0, 30) + '...');
        this.oldToken = oldToken;
        await this.handleTokenChange(oldToken, token);
      }

      // Mettre à jour le token stocké
      this.fcmToken = token;
      try {
        localStorage.setItem('fcm_token', this.fcmToken);
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du token FCM:', error);
      }
      
      // Log seulement si c'est la première fois ou si on est en mode debug
      if (!oldToken) {
        console.log('✅ Token FCM obtenu pour la première fois:', this.fcmToken.substring(0, 30) + '...');
      }
      
      return this.fcmToken;
    } catch (error) {
      console.error('Erreur lors de l\'obtention du token FCM:', error);
      throw error;
    }
  }

  /**
   * Gérer le changement de token FCM
   * Met à jour automatiquement le token dans la base de données
   */
  async handleTokenChange(oldToken, newToken) {
    try {
      // Vérifier si l'utilisateur est connecté
      const user = localStorage.getItem('user');
      if (!user) {
        console.log('Utilisateur non connecté, token non mis à jour');
        return;
      }

      // Mettre à jour le token dans la base de données
      const response = await api.put('/fcm/device/token', {
        old_token: oldToken,
        new_token: newToken
      });

      console.log('✅ Token FCM mis à jour automatiquement:', response.data);
      
      // Déclencher un événement pour notifier les composants
      window.dispatchEvent(new CustomEvent('fcmTokenRefreshed', {
        detail: { oldToken, newToken }
      }));

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du token FCM:', error);
      // Si l'ancien token n'existe pas, on enregistre le nouveau
      if (error.response?.status === 404) {
        console.log('Ancien token non trouvé, enregistrement du nouveau token...');
        try {
          await this.registerDevice();
        } catch (regError) {
          console.error('Erreur lors de l\'enregistrement du nouveau token:', regError);
        }
      }
    }
  }

  /**
   * Enregistrer le device FCM
   */
  async registerDevice(deviceData = {}) {
    if (!this.fcmToken) {
      throw new Error('Token FCM non disponible');
    }

    // Récupérer l'email de l'utilisateur depuis localStorage
    let userEmail = null;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        userEmail = user.email || null;
      }
    } catch (error) {
      console.debug('Impossible de récupérer l\'email depuis localStorage:', error);
    }

    try {
      const response = await api.post('/fcm/register', {
        fcm_token: this.fcmToken,
        device_name: deviceData.deviceName || this.getDeviceName(),
        device_type: deviceData.deviceType || this.getDeviceType(),
        platform: deviceData.platform || this.getPlatform(),
        app_version: deviceData.appVersion || '1.0.0',
        email: userEmail || deviceData.email || null
      });

      console.log('Device FCM enregistré:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du device FCM:', error);
      throw error;
    }
  }

  /**
   * Désactiver le device FCM
   */
  async unregisterDevice() {
    if (!this.fcmToken) {
      throw new Error('Token FCM non disponible');
    }

    try {
      const response = await api.post('/fcm/unregister', {
        fcm_token: this.fcmToken
      });

      console.log('Device FCM désactivé:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la désactivation du device FCM:', error);
      throw error;
    }
  }

  /**
   * Supprimer le device FCM
   */
  async deleteDevice() {
    if (!this.fcmToken) {
      throw new Error('Token FCM non disponible');
    }

    try {
      const response = await api.delete('/fcm/device', {
        data: { fcm_token: this.fcmToken }
      });

      console.log('Device FCM supprimé:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du device FCM:', error);
      throw error;
    }
  }

  /**
   * Récupérer les devices de l'utilisateur
   */
  async getUserDevices() {
    try {
      const response = await api.get('/fcm/devices');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des devices:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour les informations du device
   */
  async updateDevice(updateData) {
    if (!this.fcmToken) {
      throw new Error('Token FCM non disponible');
    }

    try {
      const response = await api.put('/fcm/device', {
        fcm_token: this.fcmToken,
        ...updateData
      });

      console.log('Device FCM mis à jour:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du device FCM:', error);
      throw error;
    }
  }

  /**
   * Marquer le device comme vu (heartbeat)
   */
  async markAsSeen() {
    if (!this.fcmToken) {
      return;
    }

    try {
      await api.post('/fcm/heartbeat', {
        fcm_token: this.fcmToken
      });
    } catch (error) {
      console.error('Erreur lors du heartbeat:', error);
    }
  }

  /**
   * Obtenir le nom du device
   */
  getDeviceName() {
    const userAgent = navigator.userAgent;
    
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return 'iPhone/iPad';
    } else if (/Android/.test(userAgent)) {
      return 'Android';
    } else if (/Windows/.test(userAgent)) {
      return 'Windows PC';
    } else if (/Mac/.test(userAgent)) {
      return 'Mac';
    } else if (/Linux/.test(userAgent)) {
      return 'Linux PC';
    }
    
    return 'Device inconnu';
  }

  /**
   * Obtenir le type de device
   */
  getDeviceType() {
    const userAgent = navigator.userAgent;
    
    if (/iPhone|iPad|iPod|Android/.test(userAgent)) {
      return 'mobile';
    } else if (/Tablet|iPad/.test(userAgent)) {
      return 'tablet';
    }
    
    return 'desktop';
  }

  /**
   * Obtenir la plateforme
   */
  getPlatform() {
    const userAgent = navigator.userAgent;
    
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return 'ios';
    } else if (/Android/.test(userAgent)) {
      return 'android';
    }
    
    return 'web';
  }

  /**
   * Vérifier si FCM est supporté
   */
  isFCMSupported() {
    return this.isSupported;
  }

  /**
   * Obtenir le token FCM actuel
   */
  getToken() {
    return this.fcmToken;
  }

  /**
   * Vérifier si le device est enregistré
   */
  async isRegistered() {
    try {
      const response = await this.getUserDevices();
      return response.devices.some(device => device.fcm_token === this.fcmToken);
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'enregistrement:', error);
      return false;
    }
  }
}

// Export singleton
export default new FCMService();
